import { Link } from 'react-router-dom';
import LearnPage from './LearnPage';
import LearnTrapBuyExampleCard from '@/components/LearnTrapBuyExampleCard';

const FAQS = [
  {
    q: 'What is a dividend trap?',
    a: 'A dividend trap is a stock with a high headline yield that is unsustainable — either because the company can\u2019t afford to keep paying it (a coming cut) or because the high yield exists only because the share price has collapsed. Investors chase the yield, get hit with a dividend cut + further price decline, and end up worse off than holding a lower-yielding stable name.',
  },
  {
    q: 'What yield level is "too high"?',
    a: 'There is no universal number, but a useful rule: if the yield is more than 2-3x the sector average and the stock has dropped 20%+ in the past year, you are almost certainly looking at a trap. AT&T at 8% in 2021 was a classic example — the yield existed because the stock had been falling for years; in February 2022 the company cut the dividend by ~46% and spun off WarnerMedia in the same announcement.',
  },
  {
    q: 'How do I tell a real high-yielder from a trap?',
    a: 'Five checks: (1) payout ratio under 80% (under 90% for REITs/BDCs), (2) revenue and EPS trending up or stable over 3 years, (3) at least 3-5 years of dividend stability or growth, (4) sector context — high yields are normal for REITs/utilities/MLPs and abnormal for tech/growth, (5) recent news flow — is the company facing structural threats? Two failing checks = caution; three failing = walk away.',
  },
  {
    q: 'Does DiviDrip flag dividend traps?',
    a: 'Yes. The Dividend Triangle scoring system (Revenue × EPS × Dividend growth, weighted by payout ratio) automatically surfaces trap flags on stocks that fail the underlying-fundamentals check despite a tempting yield. Open any ticker and click the Triangle tab inside the Stock Modal — when the detector fires you\'ll see a rose-colored "Potential Dividend Trap" banner with a clear sentence (e.g. "Dividend is growing while EPS is shrinking — payout may not be sustainable") plus a "Why this score?" panel with tone-coded bullet points spelling out exactly which Revenue / EPS / Dividend / payout-ratio fundamentals drove the call.',
  },
  {
    q: 'Can a stock recover from a dividend cut?',
    a: 'Sometimes — but usually only after years and only if the cut was strategic rather than forced. Companies that cut to fund a transformation (Disney during streaming buildout) eventually recover; companies that cut because earnings collapsed (legacy telecom, struggling REITs) often spiral. Either way, the cut day itself usually drops the stock 15-30% as yield-chasers exit en masse.',
  },
];

export default function LearnDividendTraps() {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map(({ q, a }) => ({
      '@type': 'Question',
      name: q,
      acceptedAnswer: { '@type': 'Answer', text: a },
    })),
  };

  return (
    <LearnPage
      title="Dividend Trap Stocks — How to Spot a Yield That's Too Good (2026) | DiviDrip"
      description="A practical 5-point checklist for separating sustainable high yields from dividend traps. Payout ratio, EPS trend, sector context, and the Triangle Score."
      canonical="https://twylightcrow.com/learn/dividend-traps"
      guidePath="/learn/dividend-traps"
      sectionLabel="Metrics & Evaluation"
      sectionAnchor="metrics"
      kicker="Learn · Risk awareness"
      h1="Dividend Trap Stocks — Spotting Yields That Lie"
      datePublished="2026-05-23"
      jsonLd={jsonLd}
      related={[
        { to: '/learn/dividend-triangle', label: 'Reading the Dividend Triangle Score' },
        { to: '/learn/five-numbers-to-check', label: 'The 5 numbers to check before any buy' },
        { to: '/learn/yield-on-cost', label: 'Yield on cost explained' },
        { to: '/learn/dividend-kings-vs-aristocrats-vs-achievers', label: 'Dividend Kings vs Aristocrats vs Achievers' },
        { to: '/learn/reits-vs-bdcs-vs-cefs', label: 'REITs vs BDCs vs CEFs — high-yield vehicles compared' },
        { to: '/learn/drip-vs-cash-dividends', label: 'DRIP vs cash dividends — which to choose?' },
        { to: '/glossary', label: 'Glossary — every dividend term defined' },
      ]}
    >
      <p>
        Every dividend investor has had this moment. You scroll a high-yield screener,
        find a name paying 11%, get excited, and try to figure out why nobody else is
        buying. The answer is usually one of two things: the market knows the dividend
        is about to be cut, or the market knows something structural has changed about
        the business and the share price has collapsed to compensate.
      </p>
      <p>
        These are <strong>dividend traps</strong>. Here is the 5-point check that
        separates them from real high-yielders.
      </p>

      <h2>The 5-point trap check</h2>

      <h3>1. Payout ratio &lt; 80% (or &lt; 90% for REITs/BDCs)</h3>
      <p>
        Payout ratio = (annual dividend per share) ÷ (annual EPS). If a company is
        distributing more than they earn, the dividend is being funded by debt, asset
        sales, or the prayer that earnings recover. Sustainable dividend growth requires
        room to grow within current earnings.
      </p>
      <p>
        REITs and BDCs run higher payout ratios by design (legal requirement to distribute
        90%+ of taxable income) — for them, the threshold is the AFFO or NII coverage
        ratio, not GAAP earnings. Use the per-vehicle-correct metric.
      </p>

      <h3>2. Revenue + EPS trending up or stable (3+ years)</h3>
      <p>
        A growing company can grow the dividend even from a stretched payout ratio
        because the denominator (earnings) is expanding. A shrinking company eventually
        runs out of room. Plot revenue and EPS for the last 3 years — if both are
        declining, the dividend is on borrowed time even if today&apos;s payout ratio
        looks OK.
      </p>

      <h3>3. 3-5+ years of dividend stability or growth</h3>
      <p>
        A company that has consistently raised the dividend for a decade has reputational
        capital invested in keeping the streak. Management is internally and externally
        committed to the payout. A company that has cut once in living memory has
        already shown the dividend is negotiable in a crisis.
      </p>

      <h3>4. Sector context</h3>
      <p>
        A 5% yield is normal for a utility, a midstream MLP, or a healthcare REIT. A 5%
        yield from a software company is a red flag — software companies don&apos;t
        typically yield 5% unless the share price has fallen. Always compare the yield
        to the sector average, not the broad market.
      </p>

      <h3>5. Recent news flow / structural threats</h3>
      <p>
        Final check: spend 5 minutes reading recent news. Is the business facing a
        regulatory threat, a customer concentration risk, a debt wall coming due,
        accounting investigations, executive departures? If three or more of these
        appear, the high yield is probably the market pricing in either a cut or a
        bankruptcy.
      </p>

      <h2>Worked examples</h2>

      <h3>The legendary trap: AT&amp;T 2017-2022</h3>
      <p>
        AT&amp;T spent five years yielding 6-8%, attracting income investors. The
        payout ratio drifted from 80% to 110%+ as the company funded the dividend with
        debt to also pay for media-business acquisitions. Then in February 2022 management
        cut the dividend by ~46% and simultaneously spun off Warner. Holders who chased
        the yield in 2018-21 watched their dividends and their principal both shrink.
      </p>
      <p>
        Trap signals available in real time: payout ratio over 100%, EPS flat-to-declining,
        massive debt load, sector context (legacy telecom under structural competitive
        pressure), and constant analyst warnings about the dividend.
      </p>

      <h3>The high-yielder that wasn&apos;t a trap: Altria 2020-2024</h3>
      <p>
        Altria has yielded 7-9% for years. The headline looks identical to a trap. But:
        payout ratio sits around 75%, EPS has grown modestly, the dividend has been
        raised every year for 50+ years (Dividend King), and the sector (tobacco) has
        sustainably high yields by structural design. Different story entirely — and
        holders who passed the 5-point check have been rewarded.
      </p>

      <h2>The Triangle Score — DiviDrip&apos;s built-in trap detector</h2>
      <p>
        Every stock on DiviDrip has a <strong>Dividend Triangle</strong> score that
        combines three growth legs (Revenue, EPS, Dividend) weighted by payout-ratio
        stability. The score lives on its own <strong>Triangle</strong> tab inside the
        Stock Modal — open any ticker and click the tab to see the three legs broken
        out, the overall score, and a trap-flag warning when the legs disagree (e.g.
        dividend up 30% while EPS is down 15%). A small triangle-score chip also
        renders inline next to ticker rows once you&apos;ve viewed the Triangle tab
        for that stock — color-coded green/blue/amber/red so you can spot weak
        scores at a glance.
      </p>
      <p>
        When the trap detector fires you&apos;ll see two things stacked at the top of
        the Triangle tab, above the radar chart:
      </p>
      <ul>
        <li>
          A <strong>rose &quot;Potential Dividend Trap&quot; banner</strong> with a
          clear sentence explaining the disagreement — e.g.{' '}
          <em>&quot;Dividend is growing while EPS is shrinking — payout may not be
          sustainable&quot;</em> or <em>&quot;Dividend CAGR (15.2%) is growing far
          faster than EPS (1.8%) — payout ratio is likely rising fast.&quot;</em>
        </li>
        <li>
          A collapsible <strong>&quot;Why this score?&quot;</strong> panel with up to
          three tone-coded bullets (green = positive, amber = caution, rose = negative,
          sky = info) spelling out exactly which Revenue / EPS / Dividend / payout-ratio
          fundamentals drove the call — so you&apos;re never guessing whether the flag
          was about payout ratio, earnings, or revenue.
        </li>
      </ul>
      <p>
        It is not magic; it is the 5-point check above automated and applied to
        every dividend payer in the universe.
      </p>

      <h2>FAQ</h2>
      <dl className="learn-faq">
        {FAQS.map(({ q, a }) => (
          <div key={q} className="my-4">
            <dt className="font-semibold text-foreground">{q}</dt>
            <dd className="mt-1 text-foreground/85">{a}</dd>
          </div>
        ))}
      </dl>

      <h2>Try it</h2>
      <LearnTrapBuyExampleCard />
      <p>
        Open <Link to="/dashboard">DiviDrip</Link>, sort the main stock table by Yield
        descending, and look at the top 20. Recent dividend cuts show up as a red{' '}
        <strong>Cut -X%</strong> badge directly on the row. Open any tempting
        high-yielder and check the Triangle tab — a low score plus a trap flag is
        the cluster you&apos;re trying to avoid. Then look for the rare green-tier
        high-yielder (often a quality REIT or established midstream); the Triangle
        score is what confirms it.
      </p>
    </LearnPage>
  );
}
